import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { subscribeToNotifications, removeNotification, NotificationType } from '../../utils/notificationUtils';

const NotificationsContainer = styled.div`
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 400px;
  width: calc(100% - 2rem);
`;

const NotificationItem = styled(motion.div)`
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: flex-start;
  background: #ffffff;
  border-left: 4px solid ${props => {
    switch (props.type) {
      case NotificationType.SUCCESS:
        return 'var(--success)';
      case NotificationType.ERROR:
        return 'var(--danger)';
      case NotificationType.WARNING:
        return 'var(--warning)';
      case NotificationType.INFO:
      default:
        return 'var(--primary)';
    }
  }};
`;

const NotificationIcon = styled.div`
  margin-right: 0.75rem;
  font-size: 1.25rem;
  color: ${props => {
    switch (props.type) {
      case NotificationType.SUCCESS:
        return 'var(--success)';
      case NotificationType.ERROR:
        return 'var(--danger)';
      case NotificationType.WARNING:
        return 'var(--warning)';
      case NotificationType.INFO:
      default:
        return 'var(--primary)';
    }
  }};
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: var(--text-primary);
`;

const NotificationMessage = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 1rem;
  padding: 0.25rem;
  margin-left: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: var(--text-primary);
  }
`;

const getNotificationIcon = (type) => {
  switch (type) {
    case NotificationType.SUCCESS:
      return '✓';
    case NotificationType.ERROR:
      return '✕';
    case NotificationType.WARNING:
      return '⚠';
    case NotificationType.INFO:
    default:
      return 'ℹ';
  }
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    // Subscribe to notification changes
    const unsubscribe = subscribeToNotifications((notificationStore) => {
      setNotifications(notificationStore);
    });
    
    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  const handleClose = (id) => {
    removeNotification(id);
  };

  return (
    <NotificationsContainer>
      <AnimatePresence>
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            type={notification.type}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            transition={{ duration: 0.2 }}
          >
            <NotificationIcon type={notification.type}>
              {getNotificationIcon(notification.type)}
            </NotificationIcon>
            <NotificationContent>
              {notification.title && (
                <NotificationTitle>{notification.title}</NotificationTitle>
              )}
              <NotificationMessage>{notification.message}</NotificationMessage>
            </NotificationContent>
            <CloseButton onClick={() => handleClose(notification.id)}>
              ✕
            </CloseButton>
          </NotificationItem>
        ))}
      </AnimatePresence>
    </NotificationsContainer>
  );
};

export default Notifications;
